/**
 * Pure helpers for the configuration-driven Kanban organism.
 */

import type { ReactNode } from 'react';
import {
  closestCorners,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
} from '@dnd-kit/core';
import type {
  KanbanColumnConfig,
  KanbanDropPreview,
  KanbanFeatures,
  KanbanFilterDef,
  KanbanGroupOption,
  KanbanItemBase,
  KanbanSortOption,
} from './Kanban.config.types';
import { COLUMN_SORT_ID_PREFIX, fromColumnSortId } from './Kanban.styles';

export { fromColumnSortId, toColumnSortId } from './Kanban.styles';
export type { KanbanDropPreview } from './Kanban.config.types';

export const DEFAULT_KANBAN_FEATURES: Required<
  Omit<KanbanFeatures, 'wipBehavior'>
> &
  Pick<KanbanFeatures, 'wipBehavior'> = {
  cardDrag: true,
  cardReorder: false,
  columnDrag: false,
  columnManage: false,
  columnCollapse: false,
  wipLimits: false,
  wipBehavior: 'warn',
  swimlanes: false,
  selection: false,
  search: false,
  filters: false,
  sorting: false,
  density: false,
  views: false,
  bulkActions: false,
};

export function resolveKanbanFeatures(
  features?: KanbanFeatures
): Required<Omit<KanbanFeatures, 'wipBehavior'>> & {
  wipBehavior: NonNullable<KanbanFeatures['wipBehavior']>;
} {
  return {
    ...DEFAULT_KANBAN_FEATURES,
    ...features,
    wipBehavior: features?.wipBehavior ?? DEFAULT_KANBAN_FEATURES.wipBehavior!,
  };
}

export function normalizeColumnOrder(
  columns: KanbanColumnConfig[],
  order?: string[]
): string[] {
  const ids = new Set(columns.map((c) => c.id));
  const result: string[] = [];
  for (const id of order ?? []) {
    if (ids.has(id) && !result.includes(id)) result.push(id);
  }
  for (const column of columns) {
    if (!result.includes(column.id)) result.push(column.id);
  }
  return result;
}

export function filterKanbanItems<TItem extends KanbanItemBase>(
  items: TItem[],
  options: {
    searchQuery?: string;
    getSearchText?: (item: TItem) => string;
    filterDefs?: KanbanFilterDef<TItem>[];
    activeFilters?: Record<string, string | null>;
    /** When set, skip defs that declare `columnIds` excluding this column. */
    columnId?: string;
  }
): TItem[] {
  const query = options.searchQuery?.trim().toLowerCase() ?? '';
  const active = options.activeFilters ?? {};
  const defs = filterDefsForColumn(options.filterDefs, options.columnId);

  return items.filter((item) => {
    if (query) {
      const text = (options.getSearchText?.(item) ?? '').toLowerCase();
      if (!text.includes(query)) return false;
    }
    for (const def of defs) {
      const value = active[def.id];
      if (value == null || value === '') continue;
      if (!def.match(item, value)) return false;
    }
    return true;
  });
}

/** Defs that apply to a column (or all defs when columnId is omitted). */
export function filterDefsForColumn<TItem extends KanbanItemBase>(
  filterDefs: KanbanFilterDef<TItem>[] | undefined,
  columnId?: string
): KanbanFilterDef<TItem>[] {
  if (!filterDefs?.length) return [];
  if (!columnId) return filterDefs;
  return filterDefs.filter(
    (def) => !def.columnIds?.length || def.columnIds.includes(columnId)
  );
}

export function sortKanbanItems<TItem extends KanbanItemBase>(
  items: TItem[],
  sortOptions: KanbanSortOption<TItem>[] | undefined,
  sortBy: string | null | undefined
): TItem[] {
  if (!sortBy || !sortOptions?.length) return items;
  const option = sortOptions.find((s) => s.id === sortBy);
  if (!option) return items;
  return [...items].sort(option.compare);
}

export function groupKanbanItems<TItem extends KanbanItemBase>(
  items: TItem[],
  groupOptions: KanbanGroupOption<TItem>[] | undefined,
  groupBy: string | null | undefined
): Array<{ groupId: string; label: ReactNode; items: TItem[] }> {
  if (!groupBy || !groupOptions?.length) {
    return [{ groupId: '__all__', label: null, items }];
  }
  const option = groupOptions.find((g) => g.id === groupBy);
  if (!option) {
    return [{ groupId: '__all__', label: null, items }];
  }

  const map = new Map<string, TItem[]>();
  for (const item of items) {
    const id = option.getGroupId(item);
    const list = map.get(id) ?? [];
    list.push(item);
    map.set(id, list);
  }

  return Array.from(map.entries()).map(([groupId, groupItems]) => ({
    groupId,
    label: option.getGroupLabel(groupId),
    items: groupItems,
  }));
}

export function countItemsInColumn<TItem extends KanbanItemBase>(
  items: TItem[],
  columnId: string
): number {
  return items.filter((item) => item.columnId === columnId).length;
}

export function isWipExceeded(
  column: KanbanColumnConfig | undefined,
  count: number
): boolean {
  if (column?.wipLimit == null) return false;
  // Exceeded = strictly over the limit (at-limit is allowed; matches drop block).
  return count > column.wipLimit;
}

function isColumnSortContainer(id: string | number): boolean {
  return String(id).startsWith(COLUMN_SORT_ID_PREFIX);
}

/** Discriminator for dual card/column drag in one DndContext. */
export function getKanbanDragType(event: {
  active: { data: { current?: Record<string, unknown> | undefined } };
}): 'card' | 'column' | null {
  const type = event.active.data.current?.type;
  if (type === 'card' || type === 'column') return type;
  return null;
}

/**
 * Previous column if any, otherwise next — used when deleting a column so
 * cards have a deterministic reassignment target.
 */
export function resolveReassignColumnId(
  columnOrder: string[],
  deletingId: string
): string | null {
  const index = columnOrder.indexOf(deletingId);
  if (index < 0) return null;
  if (index > 0) return columnOrder[index - 1] ?? null;
  return columnOrder[index + 1] ?? null;
}

export function makeKanbanDroppableId(
  columnId: string,
  options: { swimlanesEnabled: boolean; groupId?: string }
): string {
  if (options.swimlanesEnabled && options.groupId) {
    return `${options.groupId}::${columnId}`;
  }
  return columnId;
}

export function resolveKanbanDropTarget(
  overId: string | number | undefined,
  options: {
    columnsById: Map<string, unknown>;
    itemsById: Map<string, KanbanItemBase>;
    swimlanesEnabled: boolean;
    groupBy: string | null | undefined;
    groupOptions: KanbanGroupOption<KanbanItemBase>[];
  }
): { columnId: string; groupId?: string } | null {
  if (overId == null) return null;
  const id = String(overId);
  const sortedColumnId = fromColumnSortId(id);
  if (sortedColumnId) {
    if (!options.columnsById.has(sortedColumnId)) return null;
    return { columnId: sortedColumnId };
  }
  if (id.includes('::')) {
    const [groupId, columnId] = id.split('::');
    if (!columnId || !options.columnsById.has(columnId)) return null;
    return { columnId, groupId };
  }
  if (options.columnsById.has(id)) return { columnId: id };
  const item = options.itemsById.get(id);
  if (!item) return null;
  const groupOption =
    options.swimlanesEnabled && options.groupBy
      ? options.groupOptions.find((option) => option.id === options.groupBy)
      : undefined;
  return {
    columnId: item.columnId,
    groupId: groupOption?.getGroupId(item),
  };
}

export function findLaneItemsForGroup<TItem>(
  groups: Array<{ groupId: string; items: TItem[] }>,
  groupId: string | undefined,
  fallback: TItem[]
): TItem[] {
  const preferred = groupId ?? '__all__';
  return (
    groups.find((group) => group.groupId === preferred)?.items ??
    groups.find((group) =>
      groupId ? group.groupId === groupId : group.groupId === '__all__'
    )?.items ??
    fallback
  );
}

/**
 * Midpoint test with light hysteresis to reduce flicker when the probe sits
 * near the card center (same pattern as mature multi-container boards).
 */
export function isProbeBelowOverItem(
  probeY: number,
  overRect: { top: number; height: number },
  preferAfter: boolean | undefined,
  hysteresisPx = 6
): boolean {
  const midpoint = overRect.top + overRect.height / 2;
  if (preferAfter === true) return probeY >= midpoint - hysteresisPx;
  if (preferAfter === false) return probeY > midpoint + hysteresisPx;
  return probeY > midpoint;
}

/**
 * Center Y of the translated active rect — tracks the drag overlay without
 * requiring a separate pointer tracker.
 */
export function getActiveDragProbeY(active: {
  rect: {
    current: {
      translated: { top: number; height: number } | null;
    };
  };
}): number | null {
  const translated = active.rect.current.translated;
  if (!translated) return null;
  return translated.top + translated.height / 2;
}

/**
 * Resolve insertion index from geometry. Index is always relative to
 * `destinationItemIds` (active card already removed).
 */
export function resolveCardDropPreview(options: {
  overId: string;
  overType: string | undefined;
  overRect: { top: number; height: number } | null;
  probeY: number | null;
  target: { columnId: string; groupId?: string };
  droppableId: string;
  destinationItemIds: string[];
  previous?: KanbanDropPreview | null;
}): KanbanDropPreview {
  const {
    overId,
    overType,
    overRect,
    probeY,
    target,
    droppableId,
    destinationItemIds,
    previous,
  } = options;

  const base: Omit<KanbanDropPreview, 'index' | 'overItemId'> = {
    droppableId,
    columnId: target.columnId,
    groupId: target.groupId,
  };

  if (destinationItemIds.length === 0) {
    return { ...base, index: 0, overItemId: null };
  }

  const stickySameColumn =
    previous?.droppableId === droppableId ? previous : null;

  // Column body hit (no card). Never snap to the bottom blindly — that caused
  // "over item: - → index = length". Prefer last known index in this column;
  // otherwise use probe Y vs column rect (top half → 0, bottom → end).
  if (overType === 'card-drop') {
    if (stickySameColumn) {
      return {
        ...base,
        index: stickySameColumn.index,
        overItemId: null,
      };
    }
    if (probeY != null && overRect != null) {
      const midpoint = overRect.top + overRect.height / 2;
      return {
        ...base,
        index: probeY > midpoint ? destinationItemIds.length : 0,
        overItemId: null,
      };
    }
    return {
      ...base,
      index: destinationItemIds.length,
      overItemId: null,
    };
  }

  const overIndex = destinationItemIds.indexOf(overId);
  if (overIndex < 0) {
    if (stickySameColumn) {
      return {
        ...base,
        index: stickySameColumn.index,
        overItemId: stickySameColumn.overItemId,
      };
    }
    return {
      ...base,
      index: destinationItemIds.length,
      overItemId: null,
    };
  }

  let preferAfter: boolean | undefined;
  if (
    previous?.droppableId === droppableId &&
    previous.overItemId === overId
  ) {
    preferAfter = previous.index > overIndex;
  }

  const insertAfter =
    probeY != null && overRect != null
      ? isProbeBelowOverItem(probeY, overRect, preferAfter)
      : false;

  return {
    ...base,
    index: insertAfter ? overIndex + 1 : overIndex,
    overItemId: overId,
  };
}

/** Prefer card droppables when the pointer intersects both a card and its column. */
export function preferCardCollisions(
  collisions: ReturnType<CollisionDetection>,
  droppableContainers: Parameters<CollisionDetection>[0]['droppableContainers']
): ReturnType<CollisionDetection> {
  if (collisions.length === 0) return collisions;
  const byId = new Map(
    droppableContainers.map((container) => [String(container.id), container])
  );
  const cardHits = collisions.filter((collision) => {
    const type = byId.get(String(collision.id))?.data.current?.type;
    return type === 'card';
  });
  return cardHits.length > 0 ? cardHits : collisions;
}

/**
 * Cards: prefer pointerWithin so empty column bodies win over adjacent cards.
 * When the pointer is inside both a card and its column body, prefer the card
 * so we do not treat the hover as a bare column drop (append-to-bottom bug).
 * Columns: only collide with other column-sort containers.
 */
export function createKanbanCollisionDetection(): CollisionDetection {
  return (args) => {
    const activeType = args.active.data.current?.type;

    if (activeType === 'column') {
      const columnContainers = args.droppableContainers.filter((container) =>
        isColumnSortContainer(container.id)
      );
      return closestCorners({
        ...args,
        droppableContainers: columnContainers,
      });
    }

    const withoutColumnSort = args.droppableContainers.filter(
      (container) => !isColumnSortContainer(container.id)
    );
    const scoped = { ...args, droppableContainers: withoutColumnSort };

    const pointerHits = preferCardCollisions(
      pointerWithin(scoped),
      withoutColumnSort
    );
    if (pointerHits.length > 0) return pointerHits;

    const intersections = preferCardCollisions(
      rectIntersection(scoped),
      withoutColumnSort
    );
    if (intersections.length > 0) return intersections;

    return preferCardCollisions(closestCorners(scoped), withoutColumnSort);
  };
}

/** Hover-intent delay before showing the column insertion affordance. */
export const COLUMN_INSERT_HOVER_DELAY_MS = 50;

/**
 * Clamp a column insertion index into `[0, columnCount]`
 * (`0` = before first, `columnCount` = after last).
 */
export function clampColumnInsertIndex(
  index: number,
  columnCount: number
): number {
  if (columnCount <= 0) return 0;
  if (!Number.isFinite(index)) return columnCount;
  return Math.max(0, Math.min(Math.trunc(index), columnCount));
}

/** Insert `id` into an ordered id list at `index` (clamped). */
export function insertIdAtIndex(
  order: string[],
  id: string,
  index: number
): string[] {
  const next = [...order];
  next.splice(clampColumnInsertIndex(index, order.length), 0, id);
  return next;
}

/**
 * Resolve insert index while the Add Column dialog is open.
 * `null` pending (toolbar Add Column) means append.
 */
export function resolvePendingColumnInsertIndex(
  pendingInsertAtIndex: number | null,
  columnCount: number
): number {
  if (pendingInsertAtIndex == null) return Math.max(0, columnCount);
  return clampColumnInsertIndex(pendingInsertAtIndex, columnCount);
}

/** Accessible label for a contextual column-insert control. */
export function getColumnInsertAriaLabel(options: {
  insertAtIndex: number;
  columnTitles: string[];
}): string {
  const { insertAtIndex, columnTitles } = options;
  if (columnTitles.length === 0) return 'Add column';
  if (insertAtIndex <= 0) {
    return `Add column before ${columnTitles[0]}`;
  }
  if (insertAtIndex >= columnTitles.length) {
    return `Add column after ${columnTitles[columnTitles.length - 1]}`;
  }
  return `Add column between ${columnTitles[insertAtIndex - 1]} and ${columnTitles[insertAtIndex]}`;
}

/**
 * Plain-text column label for aria / rename when `title` may be a ReactNode.
 * Prefer explicit `titleLabel` when the visible title is not a string.
 */
export function resolveKanbanColumnTitleLabel(column: {
  title: unknown;
  titleLabel?: string;
}): string {
  if (column.titleLabel != null && column.titleLabel.trim() !== '') {
    return column.titleLabel;
  }
  if (typeof column.title === 'string' && column.title.trim() !== '') {
    return column.title;
  }
  if (typeof column.title === 'number') return String(column.title);
  return 'Column';
}

